/**
 * Sandbox Chat
 * Full-page chat for AI training. The founder asks questions, refines responses,
 * and promotes good answers as exemplars.
 * Toggle switch controls whether existing exemplars are injected into responses.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import {
  Send,
  Sparkles,
  RotateCcw,
  Award,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { authRepo, aiRepo } from '@/lib/repositories'
import { ExemplarEditor } from './ExemplarEditor'
import type { ExemplarSourceType } from '@/lib/repositories'

const MAX_MESSAGE_LENGTH = 2000

interface Message {
  id: string
  type: 'ai' | 'user'
  content: string
  timestamp: Date
}

interface SandboxChatProps {
  /** When set, resumes an existing sandbox conversation instead of creating a new one */
  resumeConversationId?: string | null
}

export function SandboxChat({ resumeConversationId }: SandboxChatProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [includeExemplars, setIncludeExemplars] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)

  // Promote state
  const [promoteData, setPromoteData] = useState<{
    question: string
    answer: string
    sourceType: ExemplarSourceType
    sourceConversationId?: string
    sourceMessageId?: string
  } | null>(null)

  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const initializedRef = useRef(false)

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight
    })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping, scrollToBottom])

  // Initialize on mount — either resume or start new
  useEffect(() => {
    if (initializedRef.current) return
    initializedRef.current = true

    if (resumeConversationId) {
      loadConversation(resumeConversationId)
    } else {
      startNewChat()
    }
  }, [resumeConversationId])

  // Handle external resume request (when resumeConversationId changes after mount)

  useEffect(() => {
    if (!initializedRef.current) return
    if (resumeConversationId && resumeConversationId !== conversationId) {
      loadConversation(resumeConversationId)
    }
  }, [conversationId, resumeConversationId])

  const loadConversation = async (convId: string) => {
    setIsLoadingHistory(true)
    try {
      setConversationId(convId)
      const history = await aiRepo.getConversationMessages(convId)

      if (history.length > 0) {
        setMessages(
          history.map((m) => ({
            id: m.id,
            type: m.role === 'user' ? ('user' as const) : ('ai' as const),
            content: m.content,
            timestamp: new Date(m.createdAt),
          }))
        )
      } else {
        setMessages([])
      }
    } catch (err) {
      console.error('Failed to load conversation:', err)
      toast.error('Failed to load conversation')
    } finally {
      setIsLoadingHistory(false)
    }
  }

  const startNewChat = async () => {
    try {
      const user = await authRepo.getCurrentUser()
      if (!user) return

      const newConvId = await aiRepo.createNewConversation(
        'sandbox',
        user.id,
        'sandbox'
      )
      setConversationId(newConvId)
      setMessages([
        {
          id: 'greeting',
          type: 'ai',
          content:
            'Sandbox mode active. Ask me anything — when I give a great answer, promote it as an exemplar. Toggle "Include Exemplars" to test how injected exemplars affect responses.',
          timestamp: new Date(),
        },
      ])
      aiRepo.saveMessage(
        newConvId,
        'assistant',
        'Sandbox mode active. Ask me anything — when I give a great answer, promote it as an exemplar. Toggle "Include Exemplars" to test how injected exemplars affect responses.'
      )
      setInput('')
    } catch (err) {
      console.error('Failed to start sandbox chat:', err)
      toast.error('Failed to start sandbox chat')
    }
  }

  const handleNewChat = async () => {
    await startNewChat()
    toast.success('New sandbox chat started')
  }

  const handleSend = async () => {
    if (!input.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    const userInput = input
    setInput('')
    setIsTyping(true)

    if (conversationId) {
      aiRepo.saveMessage(conversationId, 'user', userInput)
    }

    try {
      const response = await aiRepo.sendMessage(
        'sandbox',
        userInput,
        {},
        conversationId || undefined,
        { sandbox: !includeExemplars }
      )

      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: response.message,
        timestamp: response.timestamp,
      }

      setMessages((prev) => [...prev, aiResponse])

      if (conversationId) {
        aiRepo.saveMessage(conversationId, 'assistant', response.message)
      }
    } catch (error) {
      console.error('Sandbox AI request failed:', error)
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: `Error: ${error instanceof Error ? error.message : 'Request failed'}`,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsTyping(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Promote: find the preceding user message for a given AI message
  const handlePromote = (aiMessageIndex: number) => {
    const aiMsg = messages[aiMessageIndex]
    if (!aiMsg) return

    // Walk backwards to find the nearest user message
    let userMsg: Message | undefined
    for (let i = aiMessageIndex - 1; i >= 0; i--) {
      if (messages[i]?.type === 'user') {
        userMsg = messages[i]
        break
      }
    }

    setPromoteData({
      question: userMsg?.content || '',
      answer: aiMsg.content,
      sourceType: 'founder_edited',
      sourceConversationId: conversationId || undefined,
    })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] bg-black/30">
        <div className="flex items-center gap-3">
          {/* Include Exemplars toggle */}
          <button
            onClick={() => setIncludeExemplars((v) => !v)}
            className="flex items-center gap-2 text-xs transition-colors"
          >
            {includeExemplars ? (
              <ToggleRight className="w-5 h-5 text-emerald-400" />
            ) : (
              <ToggleLeft className="w-5 h-5 text-neutral-500" />
            )}
            <span
              className={cn(
                'font-medium',
                includeExemplars ? 'text-emerald-400' : 'text-neutral-500'
              )}
            >
              Include Exemplars
            </span>
          </button>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleNewChat}
          className="h-8 w-8 text-neutral-400 hover:text-emerald-400 hover:bg-emerald-500/10"
          title="New Chat"
        >
          <RotateCcw className="w-4 h-4" />
        </Button>
      </div>

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4"
      >
        {isLoadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-2 text-neutral-500 text-sm">
              <div className="w-4 h-4 border-2 border-neutral-600 border-t-violet-400 rounded-full animate-spin" />
              Loading conversation...
            </div>
          </div>
        ) : (
          messages.map((message, index) => (
            <div key={message.id}>
              <div
                className={cn(
                  'flex gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300',
                  message.type === 'user' && 'justify-end'
                )}
              >
                {message.type === 'ai' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/20">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                )}

                <div
                  className={cn(
                    'max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-md',
                    message.type === 'ai'
                      ? 'bg-neutral-900/80 text-neutral-100 rounded-tl-sm border border-white/[0.06] prose prose-invert prose-sm prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-headings:my-2 prose-strong:text-white max-w-none'
                      : 'bg-gradient-to-br from-violet-600 to-pink-600 text-white rounded-tr-sm whitespace-pre-line'
                  )}
                >
                  {message.type === 'ai' ? (
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  ) : (
                    message.content
                  )}
                </div>
              </div>

              {/* AI message action: Promote */}
              {message.type === 'ai' && message.id !== 'greeting' && (
                <div className="flex items-center gap-1 ml-11 mt-1">
                  <button
                    onClick={() => handlePromote(index)}
                    className="flex items-center gap-1 text-[10px] text-neutral-500 hover:text-emerald-400 transition-colors px-1.5 py-0.5 rounded hover:bg-emerald-500/10"
                  >
                    <Award className="w-3 h-3" />
                    Promote
                  </button>
                </div>
              )}
            </div>
          ))
        )}

        {isTyping && (
          <div className="flex gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/20">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="bg-neutral-900/80 rounded-2xl rounded-tl-sm px-5 py-3 border border-white/[0.06] shadow-md">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce [animation-delay:150ms]" />
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 p-4 border-t border-white/[0.06] bg-black/30 space-y-2">
        <div className="flex gap-2.5">
          <Input
            value={input}
            onChange={(e) => {
              if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                setInput(e.target.value)
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Ask the AI anything..."
            maxLength={MAX_MESSAGE_LENGTH}
            className="flex-1 bg-neutral-900/80 border border-white/[0.08] text-white placeholder:text-neutral-500 h-11 px-4 focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500/50"
            disabled={isTyping || isLoadingHistory}
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || isTyping || isLoadingHistory}
            size="icon"
            className="bg-gradient-to-br from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 text-white disabled:opacity-40 disabled:cursor-not-allowed h-11 w-11 shadow-lg shadow-violet-500/25"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center justify-between px-0.5">
          <p className="text-[11px] text-neutral-500 font-mono">
            {includeExemplars ? 'Exemplars injected' : 'Raw AI (no exemplars)'}
          </p>
          {input.length > 0 && (
            <p
              className={cn(
                'text-[11px] font-mono tabular-nums transition-colors',
                input.length >= MAX_MESSAGE_LENGTH
                  ? 'text-red-400'
                  : input.length >= MAX_MESSAGE_LENGTH - 100
                    ? 'text-amber-400'
                    : 'text-neutral-600'
              )}
            >
              {input.length}/{MAX_MESSAGE_LENGTH}
            </p>
          )}
        </div>
      </div>

      {/* Exemplar Editor (promote) */}
      <ExemplarEditor
        open={!!promoteData}
        onClose={() => setPromoteData(null)}
        prefill={promoteData || undefined}
      />
    </div>
  )
}
